import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, CopyIcon, Trash2Icon, ShieldCheckIcon, UserIcon, CalendarIcon, CheckCircleIcon, XCircleIcon, XIcon, PlusIcon, GripVerticalIcon, ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ModalDialog } from '../components/ui/ModalDialog';
import { mockReportTemplates, ReportTemplate, ReportTemplateSection } from './ReportTemplates';
import { useIsMobile, useIsTablet } from '../hooks/useMediaQuery';
import { useRole } from '../hooks/useRole';
const CURRENT_ROLE: 'superadmin' | 'medico' = 'superadmin';
const CURRENT_USER = 'Superadmin';
export function ReportTemplateDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const {
    role
  } = useRole();
  const CURRENT_USER = role === 'medico' ? 'Dr. S. Verdi' : 'Superadmin';
  const isNewDuplicate = new URLSearchParams(location.search).get('edit') === 'true';
  // Use location state if available (for duplicates), otherwise find in mock data
  const initialTemplate = location.state?.template || mockReportTemplates.find(t => t.id === id) || mockReportTemplates[0];
  const [currentTemplate, setCurrentTemplate] = useState<ReportTemplate>(initialTemplate);
  const [isEditing, setIsEditing] = useState(isNewDuplicate);
  const [editName, setEditName] = useState(currentTemplate.name);
  const [editTipoEsame, setEditTipoEsame] = useState(currentTemplate.tipoEsame);
  const [editDescrizione, setEditDescrizione] = useState(currentTemplate.descrizione);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Transform structure to have unique IDs for the builder
  const [editStructure, setEditStructure] = useState(currentTemplate.struttura.map((s, i) => ({
    ...s,
    id: `sec-${i}`
  })));
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  // Drag & Drop State
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({
      type,
      text
    });
    setTimeout(() => setToastMessage(null), 4000);
  };
  const canEdit = role === 'superadmin' || !currentTemplate.standard && currentTemplate.creatoDa === CURRENT_USER;
  const canDelete = role === 'superadmin' || !currentTemplate.standard && currentTemplate.creatoDa === CURRENT_USER;
  const handleSaveEdit = () => {
    const newErrors: Record<string, string> = {};
    if (!editName.trim()) newErrors.nome = 'Il nome del modello è obbligatorio';
    if (!editTipoEsame.trim()) newErrors.tipoEsame = 'Il tipo di esame è obbligatorio';
    editStructure.forEach(s => {
      if (!s.title.trim()) {
        newErrors[`section-${s.id}`] = 'Il titolo della sezione è obbligatorio';
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast('error', 'Compilare tutti i campi obbligatori');
      return;
    }
    // Save changes to currentTemplate
    // Preserve ownership and visibility: if it's a custom (non-standard) template,
    // keep medicoAssociato as the creator only (Superadmin + creator can see it)
    const updatedTemplate: ReportTemplate = {
      ...currentTemplate,
      name: editName,
      tipoEsame: editTipoEsame,
      descrizione: editDescrizione,
      struttura: editStructure.map(({
        id,
        ...rest
      }) => rest),
      ultimaModifica: new Date().toISOString().split('T')[0]
    };
    // If this was a duplicate (non-standard), ensure it stays personalized
    if (!updatedTemplate.standard) {
      updatedTemplate.medicoAssociato = [updatedTemplate.creatoDa];
    }
    setCurrentTemplate(updatedTemplate);
    setIsEditing(false);
    showToast('success', 'Modello salvato correttamente come "Personalizzato".');
  };
  const handleCancelEdit = () => {
    setIsEditing(false);
    setErrors({});
    setEditName(currentTemplate.name);
    setEditTipoEsame(currentTemplate.tipoEsame);
    setEditDescrizione(currentTemplate.descrizione);
    setEditStructure(currentTemplate.struttura.map((s, i) => ({
      ...s,
      id: `sec-${i}`
    })));
  };
  const handleDuplicate = () => {
    const newId = `TPL-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
    const newTemplate: ReportTemplate = {
      ...currentTemplate,
      id: newId,
      name: `${currentTemplate.name} (Copia)`,
      standard: false,
      medicoAssociato: [CURRENT_USER],
      creatoDa: CURRENT_USER,
      dataCreazione: new Date().toISOString().split('T')[0],
      ultimaModifica: new Date().toISOString().split('T')[0]
    };
    navigate(`/templates/${newId}?edit=true`, {
      state: {
        template: newTemplate
      }
    });
  };
  const handleDelete = () => {
    setShowDeleteModal(false);
    showToast('success', `Modello "${currentTemplate.name}" eliminato.`);
    setTimeout(() => navigate('/templates'), 1500);
  };
  // --- Structure Builder Handlers ---
  const handleAddSection = () => {
    const newId = Date.now().toString();
    setEditStructure([{
      id: newId,
      title: 'Nuova sezione',
      placeholder: '',
      minRows: 3
    }, ...editStructure]);
    showToast('success', 'Sezione aggiunta correttamente.');
    setTimeout(() => {
      const el = document.getElementById(`section-title-${newId}`);
      if (el) el.focus();
    }, 100);
  };
  const handleRemoveSection = (secId: string) => {
    setEditStructure(editStructure.filter(s => s.id !== secId));
  };
  const handleUpdateSection = (secId: string, field: string, value: string) => {
    setEditStructure(editStructure.map(s => s.id === secId ? {
      ...s,
      [field]: value
    } : s));
    if (errors[`section-${secId}`] && field === 'title') {
      const newErrors = {
        ...errors
      };
      delete newErrors[`section-${secId}`];
      setErrors(newErrors);
    }
  };
  const moveSection = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const newSections = [...editStructure];
      const temp = newSections[index];
      newSections[index] = newSections[index - 1];
      newSections[index - 1] = temp;
      setEditStructure(newSections);
    } else if (direction === 'down' && index < editStructure.length - 1) {
      const newSections = [...editStructure];
      const temp = newSections[index];
      newSections[index] = newSections[index + 1];
      newSections[index + 1] = temp;
      setEditStructure(newSections);
    }
  };
  // Drag & Drop Handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(img, 0, 0);
  };
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };
  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      handleDragEnd();
      return;
    }
    const newSections = [...editStructure];
    const draggedItem = newSections[draggedIndex];
    newSections.splice(draggedIndex, 1);
    newSections.splice(dropIndex, 0, draggedItem);
    setEditStructure(newSections);
    handleDragEnd();
  };
  return <div className="space-y-6 w-full min-w-0 pb-12" data-id="element-2266">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2267">
        <div className="flex items-start gap-3" data-id="element-2268">
          <button onClick={() => navigate('/templates')} className="p-2 hover:bg-clinical-gray rounded-full transition-colors text-text-secondary mt-0.5" data-id="element-2269">
            <ArrowLeftIcon size={20} data-id="element-2270" />
          </button>
          <div data-id="element-2271">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-2272">
              <h1 className="text-2xl md:text-page-title text-text-primary font-bold" data-id="element-2273">
                {isEditing ? isNewDuplicate ? editName : 'Modifica Modello' : currentTemplate.name}
              </h1>
              {!isEditing && (currentTemplate.standard ? <span className="inline-flex items-center gap-1 text-xs font-medium text-medical-green bg-medical-green-50 px-2.5 py-1 rounded-full" data-id="element-2274">
                    <ShieldCheckIcon size={13} data-id="element-2275" /> Standard
                  </span> : <span className="text-xs text-text-tertiary bg-clinical-gray-100 px-2.5 py-1 rounded-full" data-id="element-2276">
                    Personalizzato
                  </span>)}
            </div>
            {isEditing && isNewDuplicate && <div className="flex items-start gap-2 mt-1.5 bg-status-info-bg border border-status-info/20 rounded-medical px-3 py-2" data-id="element-2277">
                <CopyIcon size={14} className="text-status-info shrink-0 mt-0.5" data-id="element-2278" />
                <p className="text-xs text-text-secondary leading-relaxed" data-id="element-2279">
                  Stai modificando una copia del modello originale. Le modifiche
                  apportate qui non influiranno sul modello da cui è stata
                  generata la copia.
                </p>
              </div>}
            {!isEditing && <p className="text-sm text-text-secondary mt-0.5" data-id="element-2280">
                ID: {currentTemplate.id} · {currentTemplate.tipoEsame}
              </p>}
          </div>
        </div>
        <div className="flex items-center gap-2 pl-11 md:pl-0 flex-wrap" data-id="element-2281">
          {isEditing ? <>
              <button onClick={handleCancelEdit} className="medical-btn-secondary py-2 px-3 text-sm" title="Annulla" data-id="element-2282">
                <XIcon size={16} className={isTablet ? '' : 'mr-1.5'} data-id="element-2283" />
                {!isTablet && ' Annulla'}
              </button>
              <button onClick={handleSaveEdit} className="medical-btn-primary py-2 px-3 text-sm" title="Salva modifiche" data-id="element-2284">
                <CheckCircleIcon size={16} className={isTablet ? '' : 'mr-1.5'} data-id="element-2285" />
                {!isTablet && ' Salva modifiche'}
              </button>
            </> : <>
              <button onClick={handleDuplicate} className="medical-btn-secondary py-2 px-3 text-sm" title="Duplica" data-id="element-2286">
                <CopyIcon size={16} className={isTablet ? '' : 'mr-1.5'} data-id="element-2287" />
                {!isTablet && ' Duplica'}
              </button>
              {canEdit && <button onClick={() => setIsEditing(true)} className="medical-btn-secondary py-2 px-3 text-sm" title="Modifica" data-id="element-2288">
                  <EditIcon size={16} className={isTablet ? '' : 'mr-1.5'} data-id="element-2289" />
                  {!isTablet && ' Modifica'}
                </button>}
              {canDelete && <button onClick={() => setShowDeleteModal(true)} className="medical-btn-secondary py-2 px-3 text-sm text-status-error border-status-error/30 hover:bg-status-error-bg hover:border-status-error" title="Elimina" data-id="element-2290">
                  <Trash2Icon size={16} className={isTablet ? '' : 'mr-1.5'} data-id="element-2291" />
                  {!isTablet && ' Elimina'}
                </button>}
            </>}
        </div>
      </div>

      {/* Info Card */}
      <div className={`bg-white rounded-medical-lg shadow-card border ${isEditing ? 'border-medical-green ring-1 ring-medical-green/20' : 'border-clinical-gray-100'} p-5 md:p-6 transition-all`} data-id="element-2292">
        <h3 className="text-table-header text-text-secondary uppercase tracking-wider mb-4" data-id="element-2293">
          Informazioni Modello
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2294">
          <div className={isEditing ? 'sm:col-span-2' : ''} data-id="element-2295">
            <span className="text-xs text-text-tertiary block mb-1" data-id="element-2296">
              Nome modello *
            </span>
            {isEditing ? <>
                <input type="text" value={editName} onChange={e => {
              setEditName(e.target.value);
              if (errors.nome) setErrors({
                ...errors,
                nome: ''
              });
            }} className={`medical-input text-sm h-9 w-full ${errors.nome ? 'border-status-error ring-1 ring-status-error/20' : ''}`} data-id="element-2297" />
                {errors.nome && <p className="text-xs text-status-error mt-1" data-id="element-2298">
                    {errors.nome}
                  </p>}
              </> : <span className="text-sm font-medium text-text-primary" data-id="element-2299">
                {currentTemplate.name}
              </span>}
          </div>
          <div data-id="element-2300">
            <span className="text-xs text-text-tertiary block mb-1" data-id="element-2301">
              Tipo esame associato *
            </span>
            {isEditing ? <>
                <select value={editTipoEsame} onChange={e => {
              setEditTipoEsame(e.target.value);
              if (errors.tipoEsame) setErrors({
                ...errors,
                tipoEsame: ''
              });
            }} className={`medical-input text-sm h-9 w-full ${errors.tipoEsame ? 'border-status-error ring-1 ring-status-error/20' : ''}`} data-id="element-2302">
                  <option value="" data-id="element-2303">Seleziona un tipo di esame</option>
                  <option value="Risonanza Magnetica" data-id="element-2304">
                    Risonanza Magnetica
                  </option>
                  <option value="TAC" data-id="element-2305">TAC</option>
                  <option value="Ecografia" data-id="element-2306">Ecografia</option>
                  <option value="Radiografia" data-id="element-2307">Radiografia</option>
                  <option value="Mammografia" data-id="element-2308">Mammografia</option>
                </select>
                {errors.tipoEsame && <p className="text-xs text-status-error mt-1" data-id="element-2309">
                    {errors.tipoEsame}
                  </p>}
              </> : <span className="text-sm font-medium text-text-primary" data-id="element-2310">
                {currentTemplate.tipoEsame}
              </span>}
          </div>
          {!isEditing && <>
              <div data-id="element-2311">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-2312">
                  Tipologia
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-2313">
                  {currentTemplate.standard ? 'Standard (globale)' : 'Personalizzato'}
                </span>
              </div>
              <div data-id="element-2314">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-2315">
                  Creato da
                </span>
                <div className="flex items-center gap-1.5 text-sm font-medium text-text-primary" data-id="element-2316">
                  <UserIcon size={14} className="text-text-tertiary" data-id="element-2317" />
                  {currentTemplate.creatoDa}
                </div>
              </div>
              <div data-id="element-2318">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-2319">
                  Medico / Medici associati
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-2320">
                  {currentTemplate.medicoAssociato.join(', ')}
                </span>
              </div>
              <div data-id="element-2321">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-2322">
                  Data creazione
                </span>
                <div className="flex items-center gap-1.5 text-sm font-medium text-text-primary" data-id="element-2323">
                  <CalendarIcon size={14} className="text-text-tertiary" data-id="element-2324" />
                  {currentTemplate.dataCreazione}
                </div>
              </div>
            </>}
        </div>
      </div>

      {/* Description */}
      <div className={`bg-white rounded-medical-lg shadow-card border ${isEditing ? 'border-medical-green ring-1 ring-medical-green/20' : 'border-clinical-gray-100'} p-5 md:p-6 transition-all`} data-id="element-2325">
        <h3 className="text-table-header text-text-secondary uppercase tracking-wider mb-4" data-id="element-2326">
          Descrizione / Note
        </h3>
        {isEditing ? <textarea value={editDescrizione} onChange={e => setEditDescrizione(e.target.value)} className="medical-input w-full p-3 text-sm resize-y min-h-[80px]" data-id="element-2327" /> : <p className="text-sm text-text-primary leading-relaxed" data-id="element-2328">
            {currentTemplate.descrizione}
          </p>}
      </div>

      {/* Structure */}
      <div className={`bg-white rounded-medical-lg shadow-card border ${isEditing ? 'border-medical-green ring-1 ring-medical-green/20' : 'border-clinical-gray-100'} p-5 md:p-6 transition-all`} data-id="element-2329">
        <div className="flex items-center justify-between mb-4" data-id="element-2330">
          <h3 className="text-table-header text-text-secondary uppercase tracking-wider" data-id="element-2331">
            Struttura del Referto
          </h3>
          {isEditing && <button onClick={handleAddSection} className="text-sm font-medium text-medical-green hover:text-medical-green/80 flex items-center gap-1" data-id="element-2332">
              <PlusIcon size={16} data-id="element-2333" /> Aggiungi sezione
            </button>}
        </div>

        <div className="space-y-4" data-id="element-2334">
          {isEditing ?
        // EDIT MODE STRUCTURE BUILDER
        editStructure.map((section, index) => <div key={section.id} className="relative" data-id="element-2335">
                  {dragOverIndex === index && draggedIndex !== null && draggedIndex !== index && <div className="absolute -top-2 left-0 right-0 h-1 bg-medical-green rounded-full z-10" data-id="element-2336" />}
                  <div className={`border border-clinical-gray-200 rounded-medical-lg bg-clinical-gray-50 flex overflow-hidden transition-opacity ${draggedIndex === index ? 'opacity-50' : 'opacity-100'}`} onDragOver={e => handleDragOver(e, index)} onDrop={e => handleDrop(e, index)} onDragEnd={handleDragEnd} data-id="element-2337">
                    <div className="w-10 bg-clinical-gray-100 border-r border-clinical-gray-200 flex flex-col items-center justify-center gap-2 py-2 shrink-0" data-id="element-2338">
                      <button onClick={() => moveSection(index, 'up')} disabled={index === 0} className="text-text-tertiary hover:text-text-primary disabled:opacity-30" data-id="element-2339">
                        <ChevronUpIcon size={16} data-id="element-2340" />
                      </button>
                      <div draggable onDragStart={e => handleDragStart(e, index)} className="cursor-grab active:cursor-grabbing p-1" data-id="element-2341">
                        <GripVerticalIcon size={16} className="text-text-tertiary" data-id="element-2342" />
                      </div>
                      <button onClick={() => moveSection(index, 'down')} disabled={index === editStructure.length - 1} className="text-text-tertiary hover:text-text-primary disabled:opacity-30" data-id="element-2343">
                        <ChevronDownIcon size={16} data-id="element-2344" />
                      </button>
                    </div>
                    <div className="flex-1 p-4 space-y-3" data-id="element-2345">
                      <div className="flex items-start justify-between gap-4" data-id="element-2346">
                        <div className="flex-1" data-id="element-2347">
                          <input id={`section-title-${section.id}`} type="text" value={section.title} onChange={e => handleUpdateSection(section.id, 'title', e.target.value)} placeholder="Titolo sezione *" className={`w-full bg-transparent text-sm font-semibold text-text-primary border-b hover:border-clinical-gray-300 focus:border-medical-green focus:outline-none px-1 py-0.5 transition-colors ${errors[`section-${section.id}`] ? 'border-status-error' : 'border-transparent'}`} data-id="element-2348" />
                          {errors[`section-${section.id}`] && <p className="text-xs text-status-error mt-1 px-1" data-id="element-2349">
                              {errors[`section-${section.id}`]}
                            </p>}
                        </div>
                        <button onClick={() => handleRemoveSection(section.id)} className="p-1.5 text-text-tertiary hover:text-status-error hover:bg-status-error-bg rounded transition-colors shrink-0" data-id="element-2350">
                          <Trash2Icon size={16} data-id="element-2351" />
                        </button>
                      </div>
                      <div data-id="element-2352">
                        <textarea value={section.placeholder} onChange={e => handleUpdateSection(section.id, 'placeholder', e.target.value)} placeholder="Contenuto predefinito o placeholder..." className="medical-input text-sm min-h-[80px] resize-y bg-white" data-id="element-2353" />
                      </div>
                    </div>
                  </div>
                </div>) :
        // READ-ONLY MODE
        currentTemplate.struttura.map((section, index) => <div key={index} className="border border-clinical-gray-100 rounded-medical-lg p-4" data-id="element-2354">
                  <div className="flex items-center gap-2 mb-2" data-id="element-2355">
                    <div className="w-6 h-6 rounded-full bg-medical-green-50 text-medical-green flex items-center justify-center text-xs font-semibold shrink-0" data-id="element-2356">
                      {index + 1}
                    </div>
                    <h4 className="text-sm font-semibold text-text-primary" data-id="element-2357">
                      {section.title}
                    </h4>
                  </div>
                  <div className="ml-8" data-id="element-2358">
                    <div className="bg-clinical-gray-50 rounded-medical p-3 border border-clinical-gray-100" data-id="element-2359">
                      <p className="text-sm text-text-secondary whitespace-pre-wrap" data-id="element-2360">
                        {section.placeholder || <span className="text-text-tertiary italic" data-id="element-2361">
                            Nessun contenuto predefinito
                          </span>}
                      </p>
                    </div>
                  </div>
                </div>)}

          {isEditing && editStructure.length === 0 && <div className="text-center py-8 border-2 border-dashed border-clinical-gray-200 rounded-medical-lg" data-id="element-2362">
              <p className="text-sm text-text-secondary mb-2" data-id="element-2363">
                Nessuna sezione presente.
              </p>
              <button onClick={handleAddSection} className="medical-btn-secondary text-sm" data-id="element-2364">
                <PlusIcon size={16} className="mr-1.5" data-id="element-2365" /> Aggiungi la prima
                sezione
              </button>
            </div>}
        </div>
      </div>

      {/* Delete Modal */}
      <ModalDialog isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Elimina Modello" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setShowDeleteModal(false)} data-id="element-2367">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto bg-status-error hover:bg-status-error/90 border-status-error" onClick={handleDelete} data-id="element-2368">
              <Trash2Icon size={16} className="mr-1.5" data-id="element-2369" /> Conferma eliminazione
            </button>
          </>} data-id="element-2366">
        <div className="space-y-4" data-id="element-2370">
          <p className="text-sm text-text-secondary" data-id="element-2371">
            Sei sicuro di voler eliminare il modello{' '}
            <span className="font-semibold text-text-primary" data-id="element-2372">
              "{currentTemplate.name}"
            </span>
            ?
          </p>
          {currentTemplate.standard && <div className="bg-status-error-bg border border-status-error/20 rounded-medical-lg p-4" data-id="element-2373">
              <p className="text-sm text-status-error font-medium" data-id="element-2374">
                Attenzione: questo è un modello standard. L'eliminazione lo
                rimuoverà per tutti i medici.
              </p>
            </div>}
        </div>
      </ModalDialog>

      {/* Toast */}
      <AnimatePresence data-id="element-2375">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className={`fixed bottom-6 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-medical-lg shadow-modal border bg-white w-[calc(100%-3rem)] sm:w-auto ${toastMessage.type === 'success' ? 'border-status-success/20' : 'border-status-error/20'}`} data-id="element-2376">
            {toastMessage.type === 'success' ? <CheckCircleIcon className="text-status-success shrink-0" size={20} data-id="element-2377" /> : <XCircleIcon className="text-status-error shrink-0" size={20} data-id="element-2378" />}
            <span className="text-sm font-medium text-text-primary" data-id="element-2379">
              {toastMessage.text}
            </span>
            <button onClick={() => setToastMessage(null)} className="ml-2 p-1 text-text-tertiary hover:text-text-primary rounded-full transition-colors" data-id="element-2380">
              <XIcon size={16} data-id="element-2381" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}